import React, { type ReactElement } from 'react';
import { Box, Dialog, Stack, Typography, Divider, Switch, styled } from '@mui/material';
import Dropzone from 'react-dropzone';
import IconButton from '@mui/material/IconButton';
import { CommonErrorHelperTextV1, CommonTextField } from 'gemlux-common-components';
import { GemOrigin } from '../../../utils/enums/gem-origin.enum';
import { ActionStatusEnum } from '../../../utils/enums/action-status.enum';
import CloseSvg from '../../../assets/img/close-icon-blue.svg';
import DeleteIcon from '../../../assets/img/delete-red.svg';
import UploadGemIcon from '../../../assets/img/upload-gem-icon.svg';
import PorolisGemBankSvg from '../../../assets/img/bank.svg';
import { theme } from '../../../theme/theme';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import CustomButton from '../CustomButton/CustomButton';
import SelectColorModal from '../SelectColorModal/SelectColorModal';
import SelectShapeModal from '../SelectShapeModal/SelectShapeModal';
import useUploadGemModal from './hooks/use-uploadGemModal';

const Label = ({ text, isRequired }: { text: string; isRequired?: boolean }) => (
  <>
    <Typography variant="textBase500" component="label" id={`${text.toLowerCase()}-label`} htmlFor={text.toLowerCase()}>
      {text}
    </Typography>
    {isRequired && (
      <Typography component="label" color={theme.palette.shades.errorBrightMain}>
        &nbsp; *
      </Typography>
    )}
  </>
);
const FormField = ({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) => (
  <Box width="100%">
    <Label text={label} isRequired={required} />
    {children}
  </Box>
);

const SlimSwitch = styled(Switch)(() => ({
  width: 36,
  height: 20,
  padding: 0,
  display: 'flex',

  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#3348FF', // Active track color
        opacity: 1,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
    boxShadow: 'none',
    color: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: '#CBD3DB',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
    }),
  },
}));

function UploadGemModel(): ReactElement {
  const {
    isModelOpen,
    gemToEdit,
    handleCloseModel,
    formik,
    isColorModalOpen,
    handleOpenColorModal,
    dropdownOptions,
    specialDescriptorOptions,
    isShapeModalOpen,
    handleOpenShapeModal,
    handleFileDrop,
    getAppearanceImage,
    handleRemoveFile,
    addEditGemStatus,
    handleDeleteGem,
    isFormChanged,
    setIsColorModalOpen,
    handleSelectColor,
    currentColorField,
    setIsShapeModalOpen,
    handleSelectShape,
  } = useUploadGemModal();

  return (
    <Dialog open={isModelOpen} PaperProps={{ sx: { borderRadius: '12px' } }} fullWidth maxWidth="md">
      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="textBaseLg500">{gemToEdit == null ? 'Upload New Gem' : 'Edit Gem'}</Typography>
          <IconButton onClick={handleCloseModel}>
            <Box component="img" src={CloseSvg} />
          </IconButton>
        </Box>
        <Divider orientation="horizontal" flexItem />
        <Box sx={{ display: 'flex', p: 2 }} gap={3} padding="24px 15px">
          <Box sx={{ width: '100%' }}>
            <Box sx={{ marginBottom: 1 }}>
              <Label text="Dimensions(mm)" isRequired />
              <Stack direction="row" spacing={2} mt={0.5}>
                <FormField label="Length">
                  <Box display="flex" alignItems="center" gap={2}>
                    <CommonTextField
                      data-testid="length-input"
                      id="length"
                      sx={{ marginTop: 0.5, width: '90px' }}
                      disabled={gemToEdit != null && gemToEdit.is_scanned}
                      type="number"
                      name="length"
                      variant="outlined"
                      value={formik.values.length}
                      onChange={(event) => {
                        let { value } = event.target;

                        if (!/^\d*\.?\d*$/.test(value)) return;

                        const [intPart, decimalPart] = value.split('.');

                        if (decimalPart && decimalPart.length > 3) {
                          value = `${intPart}.${decimalPart.slice(0, 3)}`;
                        }

                        formik.setFieldValue('length', value);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.length && Boolean(formik.errors.length)}
                      inputProps={{ min: 0 }}
                      onKeyPress={(event) => {
                        if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                          event.preventDefault();
                        }
                      }}
                    />
                    <Typography variant="tableRowRegular" sx={{ color: theme.palette.text.primary }}>
                      x
                    </Typography>
                  </Box>
                </FormField>

                <FormField label="Width">
                  <Box display="flex" alignItems="center" gap={2}>
                    <CommonTextField
                      data-testid="width-input"
                      sx={{ marginTop: 0.5, width: '90px' }}
                      disabled={gemToEdit != null && gemToEdit.is_scanned}
                      type="number"
                      name="width"
                      variant="outlined"
                      value={formik.values.width}
                      onChange={(event) => {
                        let { value } = event.target;

                        if (!/^\d*\.?\d*$/.test(value)) return;

                        const [intPart, decimalPart] = value.split('.');

                        if (decimalPart && decimalPart.length > 3) {
                          value = `${intPart}.${decimalPart.slice(0, 3)}`;
                        }

                        formik.setFieldValue('width', value);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.width && Boolean(formik.errors.width)}
                      inputProps={{ min: 0 }}
                      onKeyPress={(event) => {
                        if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                          event.preventDefault();
                        }
                      }}
                    />
                    <Typography variant="tableRowRegular" sx={{ color: theme.palette.text.primary }}>
                      x
                    </Typography>
                  </Box>
                </FormField>
                <FormField label="Depth">
                  <CommonTextField
                    data-testid="depth-input"
                    sx={{ marginTop: 0.5, width: '90px' }}
                    disabled={gemToEdit != null && gemToEdit.is_scanned}
                    type="number"
                    name="depth"
                    variant="outlined"
                    value={formik.values.depth}
                    onChange={(event) => {
                      let { value } = event.target;

                      if (!/^\d*\.?\d*$/.test(value)) return;

                      const [intPart, decimalPart] = value.split('.');

                      if (decimalPart && decimalPart.length > 3) {
                        value = `${intPart}.${decimalPart.slice(0, 3)}`;
                      }

                      formik.setFieldValue('depth', value);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.depth && Boolean(formik.errors.depth)}
                    inputProps={{ min: 0 }}
                    onKeyPress={(event) => {
                      if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                        event.preventDefault();
                      }
                    }}
                  />
                </FormField>
              </Stack>
              {(formik.touched.length || formik.touched.width || formik.touched.depth) &&
                (formik.errors.length || formik.errors.width || formik.errors.depth) && (
                  <Box display="flex">
                    <CommonErrorHelperTextV1
                      errorText={
                        ((formik.touched.length || formik.touched.width || formik.touched.depth) &&
                          (formik.errors.length || formik.errors.width || formik.errors.depth)) ||
                        ''
                      }
                    />
                  </Box>
                )}
            </Box>
            <Stack direction="row" spacing={2}>
              <FormField label="Weight (ct)" required>
                <CommonTextField
                  data-testid="weight-input"
                  sx={{ marginTop: 0.5 }}
                  type="number"
                  name="weight"
                  variant="outlined"
                  value={formik.values.weight}
                  onChange={(event) => {
                    let { value } = event.target;

                    if (!/^\d*\.?\d*$/.test(value)) return;

                    const [intPart, decimalPart] = value.split('.');

                    if (decimalPart && decimalPart.length > 3) {
                      value = `${intPart}.${decimalPart.slice(0, 3)}`;
                    }

                    formik.setFieldValue('weight', value);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.weight && Boolean(formik.errors.weight)}
                  helperText={formik.touched.weight && formik.errors.weight}
                  inputProps={{ min: 0 }}
                  onKeyPress={(event) => {
                    if (event.key === '-' || event.key === 'e' || event.key === 'E') {
                      event.preventDefault();
                    }
                  }}
                />
              </FormField>
              <Box width="50%">
                <input type="hidden" />
              </Box>
            </Stack>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                my: '16px',
              }}
            />
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormField label="Primary Color" required>
                  <CommonTextField
                    data-testid="primary-color-Dropdown"
                    sx={{
                      marginTop: 0.5,
                      cursor: formik.values.color && !gemToEdit?.is_scanned ? 'pointer' : 'default',
                      '& .MuiInputBase-root': {
                        cursor: formik.values.color && !gemToEdit?.is_scanned ? 'pointer' : 'default',
                      },
                      '& .MuiOutlinedInput-input': {
                        pointerEvents: 'none',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        fontFamily: 'Inter',
                        fontSize: '15px',
                        color: theme.palette.shades.placeHolder,
                        opacity: 0.7,
                      },
                    }}
                    type="text"
                    name="color"
                    variant="outlined"
                    placeholder="Select"
                    value={formik.values.color}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled={gemToEdit != null && gemToEdit.is_scanned}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.color && !isColorModalOpen && Boolean(formik.errors.color)}
                    helperText={formik.touched.color && formik.errors.color}
                    onClick={() => {
                      if (gemToEdit != null && gemToEdit.is_scanned) return;
                      handleOpenColorModal('primary');
                    }}
                  />
                </FormField>
                <FormField label="Intensity">
                  <CustomDropdown
                    data-testid="intensity-dropdown"
                    disabled={gemToEdit != null && gemToEdit.is_scanned}
                    ariaLabelledBy="intensity-label"
                    options={dropdownOptions.intensity}
                    name="intensity"
                    value={formik.values.intensity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.intensity ? formik.errors.intensity : undefined}
                  />
                </FormField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormField label="Secondary Color" required>
                  <CommonTextField
                    data-testid="secondary-color-dropdown"
                    disabled={!formik.values.color || (gemToEdit != null && gemToEdit.is_scanned)}
                    sx={{
                      marginTop: 0.5,
                      cursor: formik.values.color && !gemToEdit?.is_scanned ? 'pointer' : 'default',
                      '& .MuiInputBase-root': {
                        cursor: formik.values.color && !gemToEdit?.is_scanned ? 'pointer' : 'default',
                      },
                      '& .MuiOutlinedInput-input': {
                        pointerEvents: 'none',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        fontFamily: 'Inter',
                        fontSize: '15px',
                        color: theme.palette.shades.placeHolder,
                        opacity: 0.7,
                      },
                    }}
                    type="text"
                    name="secondaryColor"
                    variant="outlined"
                    placeholder="Select"
                    value={formik.values.secondaryColor}
                    InputProps={{
                      readOnly: true,
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.secondaryColor && !isColorModalOpen && Boolean(formik.errors.secondaryColor)}
                    helperText={formik.touched.secondaryColor && formik.errors.secondaryColor}
                    onClick={() => {
                      if (!formik.values.color || (gemToEdit != null && gemToEdit.is_scanned)) return;
                      handleOpenColorModal('secondary');
                    }}
                  />
                </FormField>
                <FormField label="Special Descriptors" required={specialDescriptorOptions.length > 0}>
                  <CustomDropdown
                    data-testid="special-descriptor-dropdown"
                    disabled={
                      gemToEdit != null && gemToEdit.is_scanned
                        ? true
                        : !formik.values.secondaryColor || specialDescriptorOptions.length === 0
                    }
                    ariaLabelledBy="specialDescriptor-label"
                    options={specialDescriptorOptions?.map((descriptor) => ({
                      value: descriptor.toString(),
                      label: descriptor.toString(),
                    }))}
                    name="specialDescriptor"
                    value={formik.values.specialDescriptor}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.specialDescriptor && specialDescriptorOptions.length > 0
                        ? formik.errors.specialDescriptor
                        : undefined
                    }
                  />
                </FormField>
              </Stack>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  my: '16px',
                }}
              />
              <Stack sx={{ display: 'flex' }} direction="row" spacing={2}>
                <FormField label="Cut" required>
                  <CustomDropdown
                    data-testid="cut-dropdown"
                    ariaLabelledBy="cut-label"
                    options={dropdownOptions.cut}
                    name="cut"
                    value={formik.values.cut}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.cut ? formik.errors.cut : undefined}
                  />
                </FormField>
                <FormField label="Shape" required>
                  <CommonTextField
                    disabled={gemToEdit != null && gemToEdit.is_scanned}
                    data-testid="shape-dropdown"
                    sx={{
                      marginTop: 0.5,
                      cursor: formik.values.shape && !gemToEdit?.is_scanned ? 'pointer' : 'default',
                      '& .MuiInputBase-root': {
                        cursor: formik.values.shape && !gemToEdit?.is_scanned ? 'pointer' : 'default',
                      },
                      '& .MuiOutlinedInput-input': {
                        pointerEvents: 'none',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        fontFamily: 'Inter',
                        fontSize: '15px',
                        color: theme.palette.shades.placeHolder,
                        opacity: 0.7,
                      },
                    }}
                    type="text"
                    name="shape"
                    variant="outlined"
                    placeholder="Select"
                    value={formik.values.shape}
                    InputProps={{
                      readOnly: true,
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.shape && !isShapeModalOpen && Boolean(formik.errors.shape)}
                    helperText={formik.touched.shape && formik.errors.shape}
                    onClick={() => {
                      if (gemToEdit != null && gemToEdit.is_scanned) return;
                      handleOpenShapeModal();
                    }}
                  />
                </FormField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormField label="Clarity" required>
                  <CustomDropdown
                    data-testid="clarity-dropdown"
                    ariaLabelledBy="clarity-label"
                    options={dropdownOptions.clarity}
                    name="clarity"
                    value={formik.values.clarity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.clarity ? formik.errors.clarity : undefined}
                  />
                </FormField>
                <FormField label="Treatment" required>
                  <CustomDropdown
                    data-testid="treatment-dropdown"
                    ariaLabelledBy="treatment-label"
                    options={dropdownOptions.treatment}
                    name="treatment"
                    value={formik.values.treatment}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.treatment ? formik.errors.treatment : undefined}
                  />
                </FormField>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ marginBottom: 1 }}>
                <FormField label="Gem Lab" required>
                  <CustomDropdown
                    data-testid="certificate"
                    ariaLabelledBy="certificate-label"
                    options={dropdownOptions.certificate}
                    name="certificate"
                    value={formik.values.certificate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.certificate ? formik.errors.certificate : undefined}
                  />
                </FormField>
                <FormField label="Species" required>
                  <CustomDropdown
                    data-testid="species-dropdown"
                    ariaLabelledBy="species-label"
                    options={dropdownOptions.species}
                    name="species"
                    value={formik.values.species}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.species ? formik.errors.species : undefined}
                  />
                </FormField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <FormField label="Origin" required>
                  <CustomDropdown
                    ariaLabelledBy="origin-label"
                    options={dropdownOptions.origin}
                    data-testid="origin"
                    name="origin"
                    value={formik.values.origin}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.origin ? formik.errors.origin : undefined}
                  />
                </FormField>
                <FormField label="Location" required={formik.values.origin === GemOrigin.SriLanka}>
                  <CustomDropdown
                    disabled={formik.values.origin !== GemOrigin.SriLanka}
                    data-testid="location"
                    ariaLabelledBy="location-label"
                    options={dropdownOptions.location}
                    name="location"
                    value={formik.values.location}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.location && formik.values.origin === GemOrigin.SriLanka
                        ? formik.errors.location
                        : undefined
                    }
                  />
                </FormField>
              </Stack>
              {gemToEdit && gemToEdit.is_scanned && (
                <Stack>
                  <FormField label="">
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <SlimSwitch
                        checked={formik.values.isPublic}
                        onChange={formik.handleChange}
                        name="isPublic"
                        color="primary"
                      />
                      <Box component="img" src={PorolisGemBankSvg} />
                      <Typography variant="textBase500">List in Porolis GemBANK™</Typography>
                    </Box>
                  </FormField>
                </Stack>
              )}
            </Stack>
          </Box>
          <Box sx={{ width: '100%' }}>
            {(!formik.values.file || formik.errors.file) && !gemToEdit ? (
              <Box>
                <Dropzone data-testid="dropzone" onDrop={handleFileDrop}>
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <Box
                        {...getRootProps()}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2.5px dashed',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          borderColor:
                            formik.touched.file && formik.errors.file ? 'error.main' : theme.palette.shades.grayBorder,
                        }}
                        width="480px"
                        height="310px"
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                          }}
                        >
                          <>
                            <Stack direction="row" spacing={2} marginBottom={1}>
                              <Typography variant="title5Bold" color={theme.palette.common.black}>
                                Upload Gemstone image
                              </Typography>
                            </Stack>
                            <Box component="img" src={UploadGemIcon} data-testid="dropzone_img" />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyItems: 'center',
                                alignItems: 'center',
                                width: '120px',
                                height: '8px',
                                padding: 2,
                                border: `1px solid ${theme.palette.shades.lightBlueColor}`,
                                borderRadius: '8px',
                              }}
                            >
                              <Typography variant="calloutInterRegular" color={theme.palette.shades.lightBlueColor}>
                                Select file
                              </Typography>
                              <input style={{ padding: 2 }} data-testid="dropzone_input" {...getInputProps()} />
                            </Box>
                            <Typography
                              variant="calloutInterRegular"
                              marginTop={1}
                              color={theme.palette.common.black}
                              fontWeight={400}
                            >
                              or drop files to upload
                            </Typography>
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <Typography variant="metaInter" marginTop={1} color={theme.palette.shades.placeHolder}>
                                Accepted file formats:
                              </Typography>
                              <Typography variant="metaInter" marginTop={1} color={theme.palette.shades.placeHolder}>
                                JPEG, PNG, BMP. Maximum file size: 10MB.
                              </Typography>
                            </Box>
                          </>
                        </Box>
                      </Box>
                    </section>
                  )}
                </Dropzone>
                <Box my="16px">
                  {formik.touched.file && formik.errors.file && (
                    <CommonErrorHelperTextV1 errorText={` Error: ${formik.errors.file}`} />
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ position: 'relative', width: '480px', height: '310px' }}>
                <Box
                  component="img"
                  src={getAppearanceImage()}
                  alt="Uploaded file"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '10px',
                    border: `1px solid ${theme.palette.shades.grayBorder}`,
                  }}
                />
                {!gemToEdit && (
                  <IconButton
                    data-testid="remove-file"
                    onClick={handleRemoveFile}
                    color="error"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: '45%',
                      padding: 0.5,
                      border: `1px solid ${theme.palette.shades.grayBorder}`,
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      '&:hover': {
                        backgroundColor: '#fff',
                      },
                    }}
                  >
                    <Box component="img" src={DeleteIcon} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Divider />

        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
            padding: 20,
            backgroundColor: theme.palette.shades.tableHeadColor,
          }}
        >
          {gemToEdit == null ? (
            <Box sx={{ width: '15%' }}>
              <CustomButton
                variant="contained"
                color="primary"
                fullWidth
                loading={addEditGemStatus === ActionStatusEnum.LOADING}
                onClick={() => {
                  formik.handleSubmit();
                }}
              >
                Add Gem
              </CustomButton>
            </Box>
          ) : (
            <>
              <Box sx={{ width: '15%' }}>
                <CustomButton
                  sx={{
                    border: `1px solid ${theme.palette.shades.grayBorder}`,
                    color: theme.palette.shades.errorBrightMain,
                  }}
                  variant="outlined"
                  fullWidth
                  disabled={addEditGemStatus === ActionStatusEnum.LOADING}
                  onClick={handleDeleteGem}
                >
                  Delete Gem
                </CustomButton>
              </Box>
              <Box sx={{ width: '20px' }} />
              <Box sx={{ width: '15%' }}>
                <CustomButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  loading={addEditGemStatus === ActionStatusEnum.LOADING}
                  disabled={!isFormChanged}
                  onClick={() => {
                    formik.handleSubmit();
                  }}
                >
                  Update Gem
                </CustomButton>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <SelectColorModal
        isOpen={isColorModalOpen}
        onClose={() => setIsColorModalOpen(false)}
        onSelectColor={handleSelectColor}
        currentColorField={currentColorField}
      />
      <SelectShapeModal
        isOpen={isShapeModalOpen}
        onClose={() => setIsShapeModalOpen(false)}
        onSelectShape={handleSelectShape}
      />
    </Dialog>
  );
}

export default UploadGemModel;
